import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceCaseTypeEnum, ComplianceCaseOrigin } from "~/graphql/supergraphEnums";
import { GetEscalationCasesSliceDocument } from "~/graphql/supergraphHooks";
import { type GetEscalationCasesSliceQuery } from "~/graphql/supergraphTypes";
import { formatShortFormDateInLocalTimezone } from "~/utils/datetime";
import { ESCALATION_REVAMP_CASE_TYPES, escalationTypeLabel } from "../utils/constants";
import {
  EscalationCaseRows,
  EscalationRow,
  ProfileCaseMap,
  ProfileNameShape,
  EscalationCaseFilters,
  EscalationCaseSort,
  TextFilter,
  FilterInfo,
} from "./types";

const mapFieldToGraphQL = (field: string): string => {
  const fieldMap: Record<string, string> = {
    escalationId: "id",
    profile: "caseName",
    originCase: "sourceCaseName",
  };
  return fieldMap[field] || field;
};

const isUnsortableField = (field: string): boolean => {
  const unsortableFields = ["affectedPositions", "impact", "resolution", "type"];
  return unsortableFields.includes(field);
};

const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): EscalationCaseFilters[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: EscalationCaseFilters[] = [];
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter as [string, FilterInfo];

    if (isUnsortableField(field)) {
      return;
    }

    const graphqlField = mapFieldToGraphQL(field);
    if ("conditions" in filterInfo) {
      if (filterInfo.operator === "OR") {
        queryFilters.push({
          field: graphqlField,
          filterValues: filterInfo.conditions.map((condition: TextFilter) => condition.filter),
        });
      } else if (filterInfo.operator === "AND") {
        filterInfo.conditions.forEach((condition: TextFilter) => {
          queryFilters.push({ field: graphqlField, filterValues: [condition.filter] });
        });
      } else {
        throw Error("Unexpected operator for multiple conditions");
      }
    } else if (filterInfo.filterType === "set") {
      queryFilters.push({ field: graphqlField, filterValues: filterInfo.values });
    } else if (filterInfo.filterType === "text") {
      queryFilters.push({ field: graphqlField, filterValues: [filterInfo.filter] });
    } else {
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters;
};

const getSortVars = (sortItems: AgGrid.SortModelItem[]): EscalationCaseSort[] | null => {
  if (!sortItems) {
    return null;
  }
  const querySorts: EscalationCaseSort[] = [];
  sortItems.forEach((sort) => {
    if (isUnsortableField(sort.colId)) {
      return;
    }

    const graphqlField = mapFieldToGraphQL(sort.colId);
    querySorts.push({ field: graphqlField, sortAscending: sort.sort === "asc" });
  });
  return querySorts;
};

const computeAffectedPositions = (complianceCases: ProfileCaseMap[] | undefined): number => {
  if (!complianceCases) return 0;
  const uniqueArenaIds = new Set<number>();
  for (const mapItem of complianceCases) {
    const arenaId = mapItem.complianceCase?.externalArena?.id;
    if (typeof arenaId === "number") uniqueArenaIds.add(arenaId);
  }
  return uniqueArenaIds.size || 0;
};

const extractPositionIds = (complianceCases: ProfileCaseMap[] | undefined): string[] => {
  if (!complianceCases) return [];
  const positionIds: string[] = [];
  for (const mapItem of complianceCases) {
    const positionId = mapItem.complianceCase?.position?.id;
    if (typeof positionId === "string") {
      positionIds.push(positionId);
    }
  }
  return positionIds;
};

const getProfileDisplayName = (profile: ProfileNameShape): string => {
  if (!profile) return "";
  if (profile.legalName) return profile.legalName;
  const first = profile.firstName ?? "";
  const last = profile.lastName ?? "";
  return `${first} ${last}`.trim();
};

const rowsFromQuery = (rows: EscalationCaseRows): EscalationRow[] => {
  return rows.map((c) => ({
    escalationId: c.id,
    caseId: c.id,
    client: c.externalArena?.name ?? "",
    clientId: c.externalArena?.objectId ?? "",
    profile:
      c.name ??
      c.investingProfile?.legalName ??
      [c.investingProfile?.firstName, c.investingProfile?.lastName].filter(Boolean).join(" ") ??
      "",
    originCase:
      c.sourceCase?.name ??
      getProfileDisplayName((c.sourceCase?.investingProfile as ProfileNameShape) ?? null),
    originCaseId: c.sourceCase?.id ?? null,
    impact:
      c.origin === ComplianceCaseOrigin.SystemGenerated &&
      c.sourceCase?.caseType &&
      ESCALATION_REVAMP_CASE_TYPES.includes(c.sourceCase.caseType as ComplianceCaseTypeEnum)
        ? "Blocking"
        : "--",
    type: escalationTypeLabel(c.sourceCase?.caseType),
    creationDate: formatShortFormDateInLocalTimezone(String(c.createdAt)),
    affectedPositions: computeAffectedPositions(c.investingProfile?.complianceCases),
    positionIds: extractPositionIds(c.investingProfile?.complianceCases),
    status: c.status ?? "",
    resolution: c.resolution ?? "",
    assignee: c.assignee?.externalUser?.name ?? "",
  }));
};

type EscalationsDataSourceOpts = {
  sourceCaseId?: string | null;
  clientId?: string | null;
  escalationIds?: string[] | null;
};

export class EscalationsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  opts: EscalationsDataSourceOpts;
  constructor(opts: EscalationsDataSourceOpts = {}) {
    this.client = client;
    this.opts = opts;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const userFilters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;

    const filters: EscalationCaseFilters[] = [];
    filters.push({ field: "caseType", filterValues: ["escalation"] });

    const hasQuery = Boolean(
      this.opts.sourceCaseId || this.opts.clientId || this.opts.escalationIds?.length,
    );
    if (hasQuery) {
      if (this.opts.sourceCaseId) {
        filters.push({ field: "sourceCaseId", filterValues: [this.opts.sourceCaseId] });
      }
      if (this.opts.clientId) {
        filters.push({ field: "clientId", filterValues: [this.opts.clientId] });
      }
      if (this.opts.escalationIds?.length) {
        filters.push({ field: "id", filterValues: this.opts.escalationIds });
      }
    }

    if (userFilters) {
      filters.push(...userFilters);
    }

    this.client
      .query<GetEscalationCasesSliceQuery>({
        query: GetEscalationCasesSliceDocument,
        variables: {
          offset,
          limit,
          filters,
          sorts,
          profileCaseTypes: [
            ComplianceCaseTypeEnum.InvestorOnboarding,
            ComplianceCaseTypeEnum.Remediation,
          ],
        },

        fetchPolicy: "no-cache",
      })
      .then((result) => {
        const slice = result.data.complianceCasesSlice;
        const rows = slice?.rows ?? [];

        const hasQuery = Boolean(this.opts.sourceCaseId || this.opts.clientId);
        const filteredRows = hasQuery
          ? rows.filter((r) => {
              const matchesSource = this.opts.sourceCaseId
                ? String(r.sourceCase?.id ?? "") === String(this.opts.sourceCaseId)
                : true;
              const matchesClient = this.opts.clientId
                ? String(r.externalArena?.objectId ?? "") === String(this.opts.clientId)
                : true;
              return matchesSource && matchesClient;
            })
          : rows;
        const mapped = rowsFromQuery(filteredRows);
        const rowCount = hasQuery ? filteredRows.length : (slice?.filteredCount ?? 0);
        if (rowCount === 0) {
          params.api.showNoRowsOverlay();
        } else {
          params.api.hideOverlay();
        }
        params.success({
          rowData: mapped,
          rowCount,
        });
      })
      .catch(() => params.fail());
  }
}
